import { SlidersHorizontal, GripVertical, Loader2, Check, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSettingsStore } from '@/store/settingsStore';
import { useAuthStore } from '@/store/authStore';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// Define the column type
interface ColumnDefinition {
  id: string;
  label: string;
  visible: boolean;
  width?: number;
  sortable?: boolean;
  filterable?: boolean;
}

// Define the columns record type
interface ColumnsRecord {
  [key: string]: ColumnDefinition;
}

// Default column settings if none are available
const defaultColumns: ColumnsRecord = {
  date: { id: 'date', label: 'Date', visible: true, width: 120, sortable: true },
  description: { id: 'description', label: 'Description', visible: true, width: 200, filterable: true },
  category: { id: 'category', label: 'Category', visible: true, width: 150, filterable: true },
  amount: { id: 'amount', label: 'Amount', visible: true, width: 120, sortable: true },
  account: { id: 'account', label: 'Account', visible: true, width: 150, filterable: true },
  payee: { id: 'payee', label: 'Payee', visible: true, width: 150, filterable: true },
  status: { id: 'status', label: 'Status', visible: true, width: 120, filterable: true },
  notes: { id: 'notes', label: 'Notes', visible: true, width: 200, filterable: true },
  tags: { id: 'tags', label: 'Tags', visible: true, width: 150, filterable: true }
};

const defaultColumnOrder = ['date', 'description', 'category', 'amount', 'account', 'payee', 'status', 'notes', 'tags'];

interface SortableItemProps {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function SortableItem({ id, label, checked, onCheckedChange }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between py-2 px-3 rounded-md transition-all duration-200 ${
        isDragging 
          ? 'bg-primary/10 shadow-sm border border-primary/20' 
          : 'hover:bg-primary/5'
      }`}
      {...attributes}
    >
      <div className="flex items-center gap-2">
        <div 
          {...listeners} 
          className="cursor-grab active:cursor-grabbing touch-none hover:text-primary transition-colors"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
        <span className="text-sm select-none">
          {label}
        </span>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="data-[state=checked]:bg-primary"
      />
    </div>
  );
}

export default function ColumnSettings() {
  const { t } = useTranslation();
  const { settings, isLoading, updateSettings} = useSettingsStore();
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [initializationAttempted, setInitializationAttempted] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  
  // Extract transaction columns and column order from settings or use defaults
  const transactionColumns: ColumnsRecord = settings?.preferences?.transactionColumns || defaultColumns;
  const columnOrder: string[] = settings?.preferences?.columnOrder || defaultColumnOrder;

  // Debug the column settings when they change
  useEffect(() => {
    console.log('ColumnSettings: Current column config:', {
      tagsVisible: transactionColumns?.tags?.visible,
      columns: Object.keys(transactionColumns || {}).filter(key => transactionColumns[key].visible),
      columnsFromOrder: columnOrder?.filter(id => transactionColumns?.[id]?.visible)
    });
  }, [transactionColumns, columnOrder]);

  // Attempt to initialize settings with defaults if they're missing
  useEffect(() => {
    // Use the authenticated user ID from auth store
    const userId = user?.id;
    
    // Only attempt to initialize once and only if we have a userId but missing column settings
    if (!initializationAttempted && userId && 
        (!settings?.preferences?.transactionColumns || !settings?.preferences?.columnOrder)) {
      console.log('Initializing missing column settings with defaults');
      
      updateSettings(userId, {
        preferences: {
          ...settings?.preferences,
          transactionColumns: settings?.preferences?.transactionColumns || defaultColumns,
          columnOrder: settings?.preferences?.columnOrder || defaultColumnOrder
        }
      }).catch(err => console.error('Failed to initialize column settings:', err));
      
      setInitializationAttempted(true);
    }
  }, [settings?.preferences?.transactionColumns, settings?.preferences?.columnOrder, initializationAttempted, user?.id, updateSettings]);

  // Separate effect for column visibility updates to prevent infinite loops
  useEffect(() => {
    const userId = user?.id;
    
    // Only run this effect if we have userId and transactionColumns are initialized
    if (!userId || !settings?.preferences?.transactionColumns || !initializationAttempted) {
      return;
    }

    let needsUpdate = false;
    let updatedColumns = { ...settings.preferences.transactionColumns };
    
    // Check for notes column visibility
    if (settings.preferences.transactionColumns.notes && 
        !settings.preferences.transactionColumns.notes.visible) {
      console.log('Notes column is explicitly set to hidden, updating visibility');
      updatedColumns.notes = {
        ...settings.preferences.transactionColumns.notes,
        visible: true
      };
      needsUpdate = true;
    }
    
    // Check for tags column visibility
    if (settings.preferences.transactionColumns.tags && 
        !settings.preferences.transactionColumns.tags.visible) {
      console.log('Tags column is explicitly set to hidden, updating visibility');
      updatedColumns.tags = {
        ...settings.preferences.transactionColumns.tags,
        visible: true
      };
      needsUpdate = true;
    }
    
    // Update if either column needed changes
    if (needsUpdate) {
      updateSettings(userId, {
        preferences: {
          ...settings.preferences,
          transactionColumns: updatedColumns
        }
      }).catch(err => console.error('Failed to update column visibility:', err));
    }
  }, [
    settings?.preferences?.transactionColumns?.notes?.visible,
    settings?.preferences?.transactionColumns?.tags?.visible,
    user?.id,
    initializationAttempted,
    updateSettings
  ]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    // Use the authenticated user ID from auth store
    const userId = user?.id;

    if (active.id !== over.id && userId) {
      const oldIndex = columnOrder.indexOf(active.id);
      const newIndex = columnOrder.indexOf(over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
        setSaveStatus('saving');
        updateSettings(userId, {
          preferences: {
            ...settings?.preferences,
            columnOrder: newOrder
          }
        }).then(() => {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
        });
      }
    }
  };

  const updateTransactionColumns = (key: string, checked: boolean) => {
    // Use the authenticated user ID from auth store
    const userId = user?.id;
    if (!userId) return;

    const updatedColumns = {
      ...transactionColumns,
      [key]: {
        ...transactionColumns[key],
        visible: checked
      }
    };

    setSaveStatus('saving');
    updateSettings(userId, {
      preferences: {
        ...settings?.preferences,
        transactionColumns: updatedColumns
      }
    }).then(() => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    });
  };

  const resetColumnsToDefault = () => {
    // Use the authenticated user ID from auth store
    const userId = user?.id;
    if (!userId) return;
    
    setSaveStatus('saving');
    
    // Directly update with defaults
    updateSettings(userId, {
      preferences: {
        ...settings?.preferences,
        transactionColumns: defaultColumns,
        columnOrder: defaultColumnOrder
      }
    }).then(() => {
      setSaveStatus('saved');
      // Force a refresh after a slight delay to ensure settings are applied
      setTimeout(() => {
        setSaveStatus('idle');
        // Force reload the page to ensure all settings are refreshed
        window.location.reload();
      }, 1000);
    });
  };

  // If settings are loading, show a loading indicator
  if (isLoading) {
    return (
      <Button variant="outline" size="sm" className="h-9 gap-1.5" disabled>
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">
          {t('transactions.columns.loading')}
        </span>
      </Button>
    );
  }

  return (
    <TooltipProvider>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className={`h-9 gap-1.5 ${saveStatus === 'saved' ? 'bg-green-50 text-green-600 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800' : ''}`}
              >
                {saveStatus === 'saving' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : saveStatus === 'saved' ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <SlidersHorizontal className="h-4 w-4" />
                )}
                <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">
                  {saveStatus === 'saving' 
                    ? t('common.saving') 
                    : saveStatus === 'saved' 
                      ? t('common.saved') 
                      : t('transactions.columns.settings')}
                </span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>{t('transactions.columns.tooltip')}</p>
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className="w-64 p-2">
          <DropdownMenuLabel className="text-base font-medium px-2 py-1.5">
            {t('transactions.columns.title')}
          </DropdownMenuLabel>
          <p className="text-xs text-muted-foreground px-2 pb-2">
            {t('transactions.columns.instructions')}
          </p>
          <DropdownMenuSeparator />
          <div className="max-h-[300px] overflow-y-auto py-1">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={columnOrder}
                strategy={verticalListSortingStrategy}
              >
                {columnOrder.map((columnId) => (
                  <SortableItem
                    key={columnId}
                    id={columnId}
                    label={t(`transactions.columns.${columnId}`)}
                    checked={transactionColumns[columnId]?.visible || false}
                    onCheckedChange={(checked) => updateTransactionColumns(columnId, checked)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
          <DropdownMenuSeparator className="my-1" />
          <div className="flex justify-end p-2 gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      // Use the authenticated user ID from auth store
                      const userId = user?.id;
                      if (!userId) return;
                      
                      // Only update column widths, keeping visibility settings unchanged
                      setSaveStatus('saving');
                      
                      // Create a new object keeping visibility but resetting widths
                      const resetWidthsColumns = Object.entries(
                        settings?.preferences?.transactionColumns || {}
                      ).reduce((acc, [key, col]) => {
                        // Find default width for this column
                        const defaultWidth = defaultColumns[key]?.width || 120;
                        return {
                          ...acc,
                          [key]: {
                            ...col,
                            width: defaultWidth
                          }
                        };
                      }, {});
                      
                      updateSettings(userId, {
                        preferences: {
                          ...settings?.preferences,
                          transactionColumns: resetWidthsColumns
                        }
                      }).then(() => {
                        setSaveStatus('saved');
                        setTimeout(() => {
                          setSaveStatus('idle');
                          // Force reload the page to ensure all settings are refreshed
                          window.location.reload();
                        }, 1000);
                      });
                    }}
                    className="text-xs h-8 gap-1 text-muted-foreground hover:text-foreground"
                  >
                    <div className="flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
                        <path d="M3 3v18h18"></path>
                        <path d="M7 17l4-4"></path>
                        <path d="M17 17H7"></path>
                        <path d="M17 11H7"></path>
                      </svg>
                      {t('transactions.columns.resetWidths', 'Reset Widths')}
                    </div>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" align="end">
                  <p>{t('transactions.columns.resetWidthsTooltip', 'Reset column widths to default. This will not change which columns are visible.')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={resetColumnsToDefault}
                    className="text-xs h-8 gap-1 text-muted-foreground hover:text-foreground"
                  >
                    <RotateCcw className="h-3 w-3" />
                    {t('common.reset')}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" align="end">
                  <p>{t('transactions.columns.resetTooltip', 'Reset all columns to default settings. This will reload the page.')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}
